import React, { useEffect, useMemo, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { KpiCard, Card, CardHead, FilterBar, Badge } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState } from '../../features/shared/PageStates';
import { formatZAR, formatDate, humanize } from '../../lib/format';
import { STAGE_LABELS } from '../../lib/statusMaps';

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function KpiDashboard({ onNavigate, showToast, user }) {
  const [cards, setCards] = useState({});
  const [tasks, setTasks] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [month, setMonth] = useState(currentMonthValue());
  const [staffId, setStaffId] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      dealMgmt.dashboard(),
      dealMgmt.myTasks().catch(() => ({ tasks: [] })),
      dealMgmt.pipeline({}).catch(() => ({ deals: [] })),
      dealMgmt.team().catch(() => ({ users: [] })),
    ])
      .then(([d, t, p, tm]) => {
        setCards(d.cards || d || {});
        setTasks(t.tasks || t.items || []);
        setPipeline(p.deals || []);
        setTeam(tm.users || []);
      })
      .catch(e => {
        setError(e.message);
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const metrics = useMemo(() => {
    const openTasks = tasks.filter(t => !['done', 'completed', 'cancelled'].includes(t.status));
    const overdueTasks = openTasks.filter(t => t.due_date && new Date(t.due_date) < new Date(new Date().toDateString()));
    const doneTasks = tasks.filter(t => ['done', 'completed'].includes(t.status));

    let deals = pipeline;
    if (staffId) {
      deals = deals.filter(d => d.deal_manager_id === staffId || d.team_lead_id === staffId);
    }
    // Month stub: filter by updated_at / closed_at year-month when present
    if (month) {
      deals = deals.filter(d => {
        const stamp = d.closed_at || d.updated_at || d.created_at;
        if (!stamp) return true;
        return String(stamp).slice(0, 7) === month;
      });
    }

    const assigned = staffId
      ? deals.length
      : deals.filter(d => d.deal_manager_id === user?.id || d.team_lead_id === user?.id).length || deals.length;

    const completed = deals.filter(d => d.stage === 'closed').length;
    const active = deals.filter(d => !['closed', 'rejected', 'withdrawn'].includes(d.stage)).length;
    const capitalAtRisk = deals
      .filter(d => !['closed', 'rejected', 'withdrawn'].includes(d.stage))
      .reduce((s, d) => s + (parseFloat(d.capital_required) || 0), 0);

    const byStage = deals.reduce((acc, d) => {
      acc[d.stage] = (acc[d.stage] || 0) + 1;
      return acc;
    }, {});

    return {
      assigned,
      completed,
      active,
      capitalAtRisk,
      openTasks: openTasks.length,
      overdueTasks: overdueTasks.length,
      doneTasks: doneTasks.length,
      byStage,
      actionRequired: cards.deals_requiring_action ?? 0,
      above60: cards.above_60_days ?? 0,
      belowMargin: cards.below_30_margin ?? 0,
      amberRed: cards.end_client_amber_red ?? 0,
      directorMatch: cards.director_possible_match ?? 0,
      closedMonth: cards.closed_this_month ?? completed,
      awaitingIc: cards.awaiting_ic ?? byStage.ic_review ?? 0,
      qcQueue: cards.team_lead_qc ?? byStage.team_lead_qc ?? 0,
    };
  }, [tasks, pipeline, staffId, month, user?.id, cards]);

  if (loading) return <LoadingState label="Loading KPIs…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const myOpenTasks = tasks
    .filter(t => !['done', 'completed', 'cancelled'].includes(t.status))
    .slice(0, 8);

  return (
    <div>
      <FilterBar>
        <div>
          <label className="field-label">Month</label>
          <input className="field-input" type="month" value={month} onChange={e => setMonth(e.target.value)} />
        </div>
        <div>
          <label className="field-label">Staff</label>
          <select className="field-input" value={staffId} onChange={e => setStaffId(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">All / my view</option>
            {team.map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.fullName}</option>
            ))}
          </select>
        </div>
        <button type="button" className="btn btn-o btn-sm" onClick={load}>Refresh</button>
      </FilterBar>

      <div className="kpi-grid g5">
        <KpiCard label="Deals in scope" value={metrics.assigned} highlight />
        <KpiCard label="Active deals" value={metrics.active} />
        <KpiCard label="Completed (closed)" value={metrics.completed} />
        <KpiCard label="Closed this month" value={metrics.closedMonth} />
        <KpiCard label="Capital in flight" value={formatZAR(metrics.capitalAtRisk)} />
        <KpiCard label="My open tasks" value={metrics.openTasks} />
        <KpiCard label="Overdue tasks" value={metrics.overdueTasks} highlight={metrics.overdueTasks > 0} highlightColor="accent" />
        <KpiCard label="Tasks completed" value={metrics.doneTasks} />
        <KpiCard label="Awaiting IC" value={metrics.awaitingIc} />
        <KpiCard label="TL QC queue" value={metrics.qcQueue} />
        <KpiCard label="Requiring action" value={metrics.actionRequired} highlight={metrics.actionRequired > 0} />
        <KpiCard label="Over 60 days" value={metrics.above60} highlight={metrics.above60 > 0} highlightColor="accent" />
        <KpiCard label="Below margin" value={metrics.belowMargin} />
        <KpiCard label="Amber/Red end-clients" value={metrics.amberRed} />
        <KpiCard label="Director matches" value={metrics.directorMatch} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12 }}>
        <Card>
          <CardHead title="Pipeline mix" sub="Click a stage to open filtered pipeline" />
          <div className="card-body">
            {Object.keys(metrics.byStage).length === 0 ? (
              <EmptyState title="No deals in filter" sub="Adjust month or staff filter." />
            ) : (
              <table className="tbl">
                <thead>
                  <tr><th>Stage</th><th className="r">Count</th></tr>
                </thead>
                <tbody>
                  {Object.entries(metrics.byStage).map(([stage, count]) => (
                    <tr
                      key={stage}
                      className="clickable"
                      onClick={() => onNavigate('dm-pipeline', { dmFilter: { stage } })}
                    >
                      <td>{STAGE_LABELS[stage] || humanize(stage)}</td>
                      <td className="r fw">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <Card>
          <CardHead
            title="My tasks"
            sub={`${metrics.openTasks} open · ${metrics.overdueTasks} overdue`}
            action
            actionLabel="All tasks →"
            onAction={() => onNavigate('dm-tasks')}
          />
          {myOpenTasks.length === 0 && (
            <div className="card-body">
              <EmptyState title="No open tasks" sub="You're clear for now." />
            </div>
          )}
          {myOpenTasks.length > 0 && (
            <table className="tbl">
              <thead>
                <tr><th>Task</th><th>Due</th><th>Deal</th></tr>
              </thead>
              <tbody>
                {myOpenTasks.map(t => (
                  <tr
                    key={t.id}
                    className={t.deal_id ? 'clickable' : ''}
                    onClick={() => t.deal_id && onNavigate('dm-deal-detail', { dmDealId: t.deal_id })}
                  >
                    <td className="fw">
                      {t.title}
                      {t.due_date && new Date(t.due_date) < new Date(new Date().toDateString()) && (
                        <Badge type="danger">Overdue</Badge>
                      )}
                    </td>
                    <td>{t.due_date ? formatDate(t.due_date) : '—'}</td>
                    <td>{t.deal_ref || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

export default KpiDashboard;
